import { useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getQuote } from '../services/quotes';
import { useAppDispatch } from '@/app/store/hooks';
import { discardStaleQuote } from '../store/quoteSlice';
import { hydrateLoadedQuote } from '../store/hydrateLoadedQuote';
import { toast } from 'react-toastify';
import { useQuoteStepOrder } from './useQuoteStepOrder';
import type { MountLoadMode } from '../utils/mountLoadMode';

export const quoteKeys = {
    all: ['quote'] as const,
    detail: (id: string) => [...quoteKeys.all, 'detail', id] as const,
};

/**
 * Hook to load an existing quote from the URL parameter and populate the Redux store.
 * Used when navigating to /service-plans/new/{quoteId} to continue editing a quote.
 */
export const useQuoteLoader = (mode: MountLoadMode) => {
    const { quoteId } = useParams<{ quoteId: string }>();
    const dispatch = useAppDispatch();
    // Home-dealer-pinned (P-D1); resolves its own order since it runs outside QuoteProvider.
    const { order, defer, settled } = useQuoteStepOrder();

    // Fetches only for a fresh open; a refresh of a persisted draft is reconciled inside the provider.
    const {
        data: quote,
        isLoading,
        error,
        isSuccess,
        refetch,
    } = useQuery({
        queryKey: quoteKeys.detail(quoteId || ''),
        queryFn: () => getQuote(quoteId!),
        enabled: !!quoteId && mode === 'fresh-open',
        // Staleness after open is caught by the version precondition, not by a refetch that would overwrite edits.
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        staleTime: 5 * 60 * 1000, // 5 minutes
        // Dropped on unmount, so reopening a quote never hydrates a version older than the last save.
        gcTime: 0,
        retry: 1,
    });

    // Consumed once: a later step-order change must not re-apply it over a reloaded or edited draft.
    const consumedPayload = useRef<typeof quote>(undefined);
    useEffect(() => {
        if (!isSuccess || !quote || !settled || consumedPayload.current === quote) return;
        consumedPayload.current = quote;
        hydrateLoadedQuote(dispatch, quote, {
            order,
            defer,
            landing: { kind: 'resume' },
            announce: true,
        });
    }, [isSuccess, quote, dispatch, order, defer, settled]);

    // Handle loading errors
    useEffect(() => {
        if (error) {
            // The rejected value is an `ApiError` whose `originalError` graph is
            // unaudited per call site, so log the message alone.
            //
            // Customer data specifically CANNOT reach here: `GET /quotes/{id}` sends no
            // request body, and its failure paths return error envelopes, never the
            // quote (QuoteController::show has no error branch at all -- a failure is a
            // 401/403 from middleware or a 404 from model binding). So the message is
            // safe, and it is already the NORMALISED one: `readApiError` shapes it at
            // the interceptor before the rejection, so it carries the server's reason.
            //
            // The status is deliberately not logged alongside it. Reading it needs
            // `isApiError` from `@/services/api`, and importing that here pulls the
            // Axios instance, the Redux store and `QueryClient` into a hook that today
            // imports none of them -- a real module-graph coupling to buy one number in
            // one log line. The normalised message already names the failure.
            console.error('Failed to load quote:', error.message);
            toast.error('Failed to load quote. Please try again.', {
                position: 'bottom-right',
            });

            // The slice cannot be shown as this quote, and it may belong to
            // another customer. Reset it unless it already holds this id.
            if (quoteId) {
                dispatch(discardStaleQuote(quoteId));
            }
        }
    }, [error, quoteId, dispatch]);

    return {
        quoteId,
        isLoadingQuote: isLoading,
        loadedQuote: quote,
        loadError: error,
        refetchQuote: refetch,
        isNewQuote: !quoteId,
    };
};
